import { useParams, useNavigate } from 'react-router-dom';
import { Card } from "@/components/ui/card";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form } from "@/components/ui/form";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { WizardProvider } from "./FormWizardContext";
import { OnboardingFormValues, onboardingFormSchema } from "./OnboardingFormSchema";
import { FormExpired } from "./components/FormExpired";
import { FormContent } from "./components/FormContent";
import { useOnboardingFormData } from "./hooks/useOnboardingFormData";
import { useState, useEffect } from "react";
import { MultiPhaseTracker } from "./MultiPhaseTracker";
import { useOnboardingPhases } from "./hooks/useOnboardingPhases";
import { getPhaseData, getCurrentPhase } from "@/utils/onboardingUtils";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Hourglass, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";

interface OnboardingFormProps {
  formId?: string;
}

const OnboardingForm = ({ formId: propFormId }: OnboardingFormProps) => {
  const params = useParams();
  const formId = propFormId || params.formId; // Use prop or URL param
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { updatePhaseStatus } = useOnboardingPhases();
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [totalSteps, setTotalSteps] = useState(5); // Default
  
  const form = useForm<OnboardingFormValues>({
    resolver: zodResolver(onboardingFormSchema),
    defaultValues: {
      social_media_type: '',
      experience_rate: 0,
      content_links: [],
      chain: "L1" as "L1" | "L2",
    }
  });

  const { isLoading, isExpired, formData } = useOnboardingFormData(formId, form);
  
  // Determine the correct number of steps based on the current phase
  useEffect(() => {
    if (formData) {
      const currentPhase = getCurrentPhase(formData);
      console.log("Current phase detected:", currentPhase);
      if (currentPhase === 1) {
        setTotalSteps(4); // Phase 1: Creator, Content, Additional, Review
      } else if (currentPhase >= 3) {
        setTotalSteps(2); // Phase 3+: Invoice, Review
      } else {
        setTotalSteps(5); // Default: Creator, Content, Additional, Invoice, Review
      }
    }
  }, [formData]);
  
  // Update phase 1 to in-progress when form is loaded
  useEffect(() => {
    if (formData && formId && formData.status_phase_1 === 'pending') {
      updatePhaseStatus({
        formId: formId,
        phaseNumber: 1,
        status: 'in-progress'
      });
    }
  }, [formData, formId, updatePhaseStatus]);

  const onSubmit = async (values: OnboardingFormValues) => {
    if (!formId) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Form ID is missing"
      });
      return;
    }

    try {
      setIsSubmitting(true);
      console.log("Starting form submission with values:", values);
      
      // Ensure we have the latest form data
      const currentPhase = getCurrentPhase(formData);
      console.log("Current phase for submission:", currentPhase);
      
      // Prepare the update data - common fields for all phases
      const updateData: any = {
        kol_name: values.creator_name,
        social_media_type: values.social_media_type,
        social_media_handler: values.social_media_handler,
        social_media_name: values.social_media_name,
        agreed_rate: values.agreed_rate,
        currency_type: values.currency_type,
        region: values.region,
        content_links: Array.isArray(values.content_links) ? values.content_links : [],
        compensation_amount: values.compensation_amount,
        compensation_currency: values.compensation_currency,
        experience_rate: values.experience_rate,
        feedback: values.feedback,
        email: values.email,
        deliverables: values.deliverables,
        description: values.description, // Add description field
        collaboration_period: values.collaboration_period, // Add collaboration_period field
        pic_id: values.pic_id ? values.pic_id : null,
        updated_at: new Date().toISOString()
      };
      
      // Ensure dates are properly formatted if provided
      if (values.start_date) {
        updateData.start_date = values.start_date.toISOString();
      }
      
      if (values.end_date) {
        updateData.end_date = values.end_date.toISOString();
      }
      
      // Phase-specific data handling
      if (currentPhase === 1) {
        // Phase 1: Mark as completed and set phase 2 to in-progress
        console.log("Phase 1 submission - transitioning to Phase 2");
        updateData.status = 'in-progress';
        updateData.status_phase_1 = 'completed';
        updateData.status_phase_2 = 'in-progress';
        
      } else if (currentPhase >= 3) {
        // Phase 3+: Handle invoice completion and transition to phase 4
        console.log("Phase 3 submission - transitioning to Phase 4");
        
        if (!values.invoice_link) {
          throw new Error("Invoice link is required for Phase 3 submission");
        }
        
        // Ensure we have all required invoice data
        updateData.invoice_link = values.invoice_link;
        updateData.invoice_number = values.invoice_number;
        updateData.invoice_date = values.invoice_date;
        updateData.wallet_address = values.wallet_address;
        updateData.wallet_address_network = values.wallet_address_network;
        updateData.chain = values.chain;
        updateData.description = values.description;
        
        // Ensure amount and total are always in sync
        if (values.amount !== undefined && values.amount !== null) {
          const numericAmount = typeof values.amount === 'string' ? parseFloat(values.amount) : values.amount;
          if (!isNaN(numericAmount)) {
            updateData.amount = numericAmount;
            updateData.total = numericAmount; // Always sync total to match amount
            console.log("OnboardingForm: Synchronized total with amount:", numericAmount);
          }
        }
        
        // Update phase statuses for Phase 3 -> 4 transition
        updateData.status_phase_3 = 'completed';
        updateData.status_phase_4 = 'in-progress';
      }
      
      console.log("Updating form with data:", updateData);
      
      // Use the edge function for all phase transitions to ensure consistent handling
      console.log("Using edge function for form submission in phase:", currentPhase);
      const { data, error } = await supabase.functions.invoke('update-payment-submissions', {
        body: {
          form_id: formId,
          update_data: updateData
        }
      });
      
      if (error) {
        console.error("Edge function error:", error);
        throw error;
      }
      
      console.log("Form submission successful:", data);
      
      // Show success toast
      toast({
        title: "Form Submitted Successfully",
        description: "Your form has been submitted successfully!"
      });
      
      // Handle post-submission navigation based on phase
      if (currentPhase === 1) {
        setFormSubmitted(true);
      } else if (currentPhase >= 3 && values.invoice_link) {
        console.log("Phase 3 completed, navigating to success page");
        // Navigate to the success page immediately to show the test transaction screen
        navigate(`/onboarding-success/${formId}`);
      }
      
    } catch (error: any) {
      console.error("Error submitting form:", error);
      toast({
        variant: "destructive",
        title: "Submission Failed",
        description: error.message || "An error occurred while submitting the form"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white dark:bg-gray-900">
        <p className="text-dashboard-text-primary">Loading form data...</p>
      </div>
    );
  }

  if (isExpired) {
    // We'll still use the FormExpired component if the form isn't found
    return <FormExpired />;
  }

  const phases = getPhaseData(formData);
  const currentPhase = getCurrentPhase(formData);

  // Check if the form is in approval phase (Phase 1 completed, Phase 2 in progress)
  const isInApprovalPhase = formData && 
    ((formData.status_phase_1 === 'completed' && formData.status_phase_2 === 'in-progress') ||
     formSubmitted);

  // If the form is in approval phase, show the waiting for approval message
  if (isInApprovalPhase) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-white dark:bg-gray-900 p-4">
        <div className="w-full max-w-3xl mb-0">
          <Card className="bg-white dark:bg-gray-800 shadow-lg border-0 animate-fade-in overflow-hidden">
            <div className="p-8 text-center">
              <div className="flex flex-col items-center justify-center space-y-6">
                <div className="h-20 w-24">
                  <img 
                    src="/lovable-uploads/57a7aaa6-bf38-4409-a65a-b5c45e8026c6.png" 
                    alt="Mantle Logo" 
                    className="h-full w-full object-contain"
                  />
                </div>
                
                <Hourglass className="h-16 w-16 text-amber-500 animate-pulse" />
                
                <h1 className="text-2xl font-bold text-center text-gray-800 dark:text-white">Application Submitted Successfully</h1>
                
                <Alert className="bg-green-50 border-green-200 text-green-800 dark:bg-green-900/20 dark:text-green-300 dark:border-green-800">
                  <AlertTitle>Thank you for your submission</AlertTitle>
                  <AlertDescription>
                    Your application has been submitted and is now being reviewed. You will receive an update once the review is complete.
                  </AlertDescription>
                </Alert>
                
                {/* Progress bar to indicate waiting status */}
                <div className="w-full space-y-2">
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>Submitted</span>
                    <span>Under Review</span>
                    <span>Approved</span>
                  </div>
                  <Progress value={33} className="h-2" />
                </div>
                
                {/* New section: Preview of Invoice Generation (Locked) */}
                <div className="w-full mt-8 border border-dashed border-gray-300 dark:border-gray-700 rounded-lg p-6">
                  <div className="flex flex-col items-center space-y-4">
                    <Lock className="h-8 w-8 text-gray-400" />
                    <h3 className="text-lg font-medium text-gray-700 dark:text-gray-300">Invoice Generation</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 text-center">
                      Once your application is approved, you'll be able to generate invoices for your content creation services.
                    </p>
                    <Button variant="outline" disabled className="opacity-60 cursor-not-allowed">
                      Generate Invoice (Requires Approval)
                    </Button>
                  </div>
                </div>
                
              </div>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-white dark:bg-gray-900 p-4">
      <div className="w-full max-w-5xl mb-0">
        <Card className="bg-white dark:bg-gray-800 shadow-lg border-0 animate-fade-in overflow-hidden">
          {/* Banner Image - Now part of the card */}
          <div className="w-full">
            <img 
              src="/lovable-uploads/db8cabcc-305f-4bce-b96f-f768e367153e.png" 
              alt="Mantle Finance" 
              className="w-full h-48 object-cover"
            />
          </div>
          
          <div className="p-8">
            <div className="flex flex-col items-center justify-center space-y-6">
              <div className="h-20 w-24">
                <img 
                  src="/lovable-uploads/57a7aaa6-bf38-4409-a65a-b5c45e8026c6.png" 
                  alt="Mantle Logo" 
                  className="h-full w-full object-contain"
                />
              </div>
              <h1 className="text-2xl font-bold text-center text-gray-800 dark:text-white">Mantle Content Creator Compensation Application Form</h1>
              
              <div className="text-center text-sm text-gray-600 dark:text-gray-300 max-w-2xl">
                <p>DO NOT circulate this form. Only content creators who have signed a content creator agreement with Mantle can apply for compensation.</p>
                <p className="mt-2">
                  By submitting this form, you confirm your acceptance of the <a href="https://www.mantle.xyz/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Mantle Privacy Policy</a>, and agree that your information will be handled as described therein.
                </p>
              </div>
              
              {/* Phase Status Overview - Now only showing current phase */}
              <div className="w-full max-w-md mb-4">
                <MultiPhaseTracker 
                  phases={phases} 
                  currentPhase={currentPhase}
                  showOnlyCurrent={true}
                />
              </div>
              
              <WizardProvider totalSteps={totalSteps}>
                <Form {...form}>
                  <FormContent 
                    form={form}
                    onSubmit={onSubmit}
                    isSubmitting={isSubmitting}
                    currentPhase={currentPhase}
                    formId={formId}
                  />
                </Form>
              </WizardProvider>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}

export default OnboardingForm;
